<template>
  <el-row :gutter="16" class="kpi-cards">
    <el-col :xs="24" :sm="12" :md="6" :lg="6" :xl="6" v-for="(kpi, index) in kpis" :key="index">
      <el-card class="kpi-card" :class="`kpi-card-${index % 4}`" shadow="hover">
        <div class="kpi-content">
          <div class="kpi-header">
            <div class="kpi-icon-wrapper">
              <font-awesome-icon :icon="getKpiIcon(index)" class="kpi-icon" />
            </div>
            <div class="kpi-label">{{ kpi.label }}</div>
          </div>
          <div class="kpi-main-value">{{ kpi.value }}<span v-if="kpi.type === 'date'" class="kpi-unit"> хоног</span></div>
          <div class="kpi-secondary-value">{{ kpi.secondaryValue }}</div>
          <div class="kpi-stats" v-if="kpi.type === 'numeric'">
            <div class="kpi-stat-item">
              <span class="stat-label">Дээд:</span>
              <span class="stat-value">{{ kpi.stats.max }}</span>
            </div>
            <div class="kpi-stat-item">
              <span class="stat-label">Доод:</span>
              <span class="stat-value">{{ kpi.stats.min }}</span>
            </div>
            <div class="kpi-stat-item">
              <span class="stat-label">Тоо:</span>
              <span class="stat-value">{{ kpi.stats.count }}</span>
            </div>
          </div>
          <div class="kpi-stats" v-else-if="kpi.type === 'count'">
            <div class="kpi-stat-item">
              <span class="stat-label">Нийт бичлэг:</span>
              <span class="stat-value">{{ kpi.stats.count }}</span>
            </div>
          </div>
          <div class="kpi-stats" v-else-if="kpi.type === 'date'">
            <div class="kpi-stat-item">
              <span class="stat-label">Хамгийн бага:</span>
              <span class="stat-value">{{ kpi.stats.minDate }}</span>
            </div>
            <div class="kpi-stat-item">
              <span class="stat-label">Хамгийн их:</span>
              <span class="stat-value">{{ kpi.stats.maxDate }}</span>
            </div>
            <div class="kpi-stat-item">
              <span class="stat-label">Хоног:</span>
              <span class="stat-value">{{ kpi.stats.days }}</span>
            </div>
          </div>
        </div>
      </el-card>
    </el-col>
  </el-row>
</template>

<script>
export default {
  name: 'ReportKPICards',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    columns: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    kpis () {
      if (!this.data || this.data.length === 0) return []

      const kpiList = []

      // 1. ID багануудыг зөвхөн count хийж нэмэх (sum хийхгүй)
      const idColumns = this.getIDColumns()
      idColumns.forEach(col => {
        const count = this.data.filter(row => {
          const value = row[col]
          return value !== null && value !== undefined && value !== ''
        }).length

        kpiList.push({
          label: this.formatColumnName(col),
          value: this.formatNumber(count),
          secondaryValue: 'Нийт бичлэг',
          type: 'count',
          stats: {
            count: count
          }
        })
      })

      // 2. Date багануудыг нэмэх - хамгийн бага болон хамгийн их огноо хооронд хэдэн хоног байгааг тоолох
      const dateColumns = this.getDateColumns()
      dateColumns.forEach(col => {
        const dates = this.data
          .map(row => {
            const dateStr = row[col]
            if (!dateStr) return null
            const date = new Date(dateStr)
            return isNaN(date.getTime()) ? null : date
          })
          .filter(date => date !== null)

        if (dates.length === 0) return

        const minDate = new Date(Math.min(...dates.map(d => d.getTime())))
        const maxDate = new Date(Math.max(...dates.map(d => d.getTime())))
        const daysDiff = Math.ceil((maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24))

        kpiList.push({
          label: this.formatColumnName(col),
          value: this.formatNumber(daysDiff),
          secondaryValue: `${this.formatDate(minDate)} - ${this.formatDate(maxDate)}`,
          type: 'date',
          stats: {
            minDate: this.formatDate(minDate),
            maxDate: this.formatDate(maxDate),
            days: daysDiff
          }
        })
      })

      // 3. Тоон үзүүлэлттэй багануудыг нэмэх
      const numericColumns = this.getValidNumericColumns()
      numericColumns.forEach(col => {
        const values = this.data
          .map(row => parseFloat(row[col]))
          .filter(val => !isNaN(val) && isFinite(val))

        if (values.length === 0) return

        const sum = values.reduce((a, b) => a + b, 0)
        const avg = sum / values.length
        const max = Math.max(...values)
        const min = Math.min(...values)
        const count = values.length

        kpiList.push({
          label: this.formatColumnName(col),
          sum: this.formatNumber(sum),
          avg: this.formatNumber(avg),
          max: this.formatNumber(max),
          min: this.formatNumber(min),
          count: count,
          type: 'numeric',
          // Хамгийн чухал үзүүлэлтийг харуулах (sum эсвэл avg)
          value: this.formatNumber(sum),
          secondaryValue: `Дундаж: ${this.formatNumber(avg)}`,
          stats: {
            max: this.formatNumber(max),
            min: this.formatNumber(min),
            count: count
          }
        })
      })

      // Хамгийн ихдээ 4 KPI card харуулах
      return kpiList.slice(0, 4)
    }
  },
  methods: {
    formatColumnName (column) {
      return column
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    // ID багануудыг олох
    getIDColumns () {
      return this.columns.filter(col => {
        const lowerName = col.toLowerCase()
        // ID багана эсэхийг шалгах
        return lowerName === 'id' ||
               lowerName.endsWith('_id') ||
               lowerName.endsWith('id') ||
               lowerName.includes('_id_') ||
               (lowerName.includes('id') && this.isValidIDColumn(col))
      })
    },
    // Date багануудыг олох
    getDateColumns () {
      return this.columns.filter(col => {
        const lowerName = col.toLowerCase()
        const datePatterns = [
          'date',
          'огноо',
          'time',
          'цаг',
          'created',
          'үүсгэсэн',
          'updated',
          'өөрчлөгдсөн',
          'start',
          'эхлэх',
          'end',
          'дуусах',
          'finish',
          'заверш'
        ]

        // Date багана эсэхийг шалгах
        if (datePatterns.some(pattern => lowerName.includes(pattern))) {
          // Энэ багана нь date утга агуулж байгаа эсэхийг шалгах
          const sampleValue = this.data[0]?.[col]
          if (sampleValue) {
            const date = new Date(sampleValue)
            return !isNaN(date.getTime())
          }
        }
        return false
      })
    },
    // ID багана эсэхийг шалгах
    isValidIDColumn (columnName) {
      const lowerName = columnName.toLowerCase()
      // Хэрэв баганын нэр нь зөвхөн "id" эсвэл "id" агуулж байвал
      if (lowerName === 'id' || lowerName.endsWith('_id')) {
        return true
      }
      // Хэрэв баганын нэр нь "id" агуулж байгаа боловч тоон биш багана биш бол
      if (lowerName.includes('id')) {
        // Утасны дугаар, email гэх мэт биш эсэхийг шалгах
        const nonIDPatterns = ['phone', 'утас', 'email', 'имэйл', 'code', 'код']
        return !nonIDPatterns.some(pattern => lowerName.includes(pattern))
      }
      return false
    },
    // Тоон биш багануудыг шалгах (ID-ээс бусад)
    isNonNumericColumn (columnName) {
      const lowerName = columnName.toLowerCase()
      // ID баганыг тоон биш гэж тооцохгүй (count хийх тул)
      if (this.isValidIDColumn(columnName)) {
        return false
      }
      const nonNumericPatterns = [
        'phone',
        'утас',
        'email',
        'имэйл',
        'code',
        'код',
        'track',
        'date',
        'огноо',
        'time',
        'цаг',
        'name',
        'нэр',
        'address',
        'хаяг',
        'status',
        'төлөв'
      ]
      return nonNumericPatterns.some(pattern => lowerName.includes(pattern))
    },
    // Утгын хэмжээг шалгах - хэрэв бүх утга нь ижил урттай эсвэл маш том тоо байвал sum хийх утгагүй
    hasValidNumericRange (values) {
      if (values.length === 0) return false

      // Бүх утгын уртыг шалгах
      const lengths = values.map(v => String(v).length)
      const uniqueLengths = [...new Set(lengths)]

      // Хэрэв бүх утга нь ижил урттай (жишээ нь: 8 оронтой утасны дугаар) бол sum хийх утгагүй
      if (uniqueLengths.length === 1 && uniqueLengths[0] >= 8) {
        return false
      }

      // Хэрэв утгууд нь маш том тоо байвал (жишээ нь: 10^10-аас их) sum хийх утгагүй
      const maxValue = Math.max(...values)
      if (maxValue > 10000000000) {
        return false
      }

      return true
    },
    // Зөвхөн тоон үзүүлэлттэй багануудыг сонгох (ID болон Date багануудыг оруулахгүй)
    getValidNumericColumns () {
      return this.columns.filter(col => {
        // ID багана эсэхийг шалгах - ID багануудыг тоон үзүүлэлтээс хасах
        if (this.isValidIDColumn(col)) {
          return false
        }

        // Date багана эсэхийг шалгах - Date багануудыг тоон үзүүлэлтээс хасах
        const dateColumns = this.getDateColumns()
        if (dateColumns.includes(col)) {
          return false
        }

        // Тоон биш багана эсэхийг шалгах
        if (this.isNonNumericColumn(col)) {
          return false
        }

        // Бүх мөрөнд тоон утга байгаа эсэхийг шалгах
        const values = this.data
          .map(row => {
            const val = row[col]
            if (val === null || val === undefined || val === '') return null
            const num = parseFloat(val)
            return isNaN(num) ? null : num
          })
          .filter(val => val !== null)

        // Хэрэв тоон утга байхгүй бол хасах
        if (values.length === 0) return false

        // Хэрэв тоон утга бага байвал (жишээ нь: 50%-аас бага) хасах
        if (values.length < this.data.length * 0.5) return false

        // Утгын хэмжээг шалгах
        if (!this.hasValidNumericRange(values)) return false

        // Тоон үзүүлэлттэй багана эсэхийг шалгах (price, amount, quantity, total, sum, count гэх мэт)
        const lowerName = col.toLowerCase()
        const numericIndicators = [
          'price', 'үнэ', 'amount', 'дүн', 'quantity', 'тоо', 'total', 'нийт',
          'sum', 'нийлбэр', 'count', 'тооллого', 'avg', 'дундаж', 'average',
          'max', 'хамгийн', 'min', 'бага', 'value', 'утга', 'cost', 'зардал',
          'fee', 'шимтгэл', 'tax', 'татвар', 'discount', 'хөнгөлөлт', 'profit', 'ашиг',
          'loss', 'алдагдал', 'balance', 'үлдэгдэл', 'weight', 'жин', 'volume', 'эзлэхүүн'
        ]

        // Хэрэв баганын нэр нь тоон үзүүлэлт агуулж байвал зөв
        if (numericIndicators.some(indicator => lowerName.includes(indicator))) {
          return true
        }

        // Хэрэв дундаж утга нь 0-ээс их байвал (үнэхээр тоон үзүүлэлт) зөв
        const avg = values.reduce((a, b) => a + b, 0) / values.length
        if (avg > 0 && avg < 1000000000) {
          return true
        }

        return false
      })
    },
    formatNumber (num) {
      if (num === null || num === undefined || isNaN(num)) return '0'

      // Хэрэв тоо нь бүхэл тоо бол бүхэл тоогоор харуулах
      if (Number.isInteger(num)) {
        return num.toLocaleString('mn-MN')
      }

      // Хэрэв тоо нь бутархай бол 2 оронгоор харуулах
      return num.toLocaleString('mn-MN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    },
    getKpiIcon (index) {
      const icons = ['chart-line', 'calculator', 'chart-pie', 'chart-bar']
      return icons[index % icons.length] || 'chart-line'
    },
    formatDate (date) {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return '-'
      }
      return date.toLocaleDateString('mn-MN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.kpi-cards {
  margin-bottom: 24px;
  margin: 0 -8px;
}

.kpi-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: none;
  transition: all 0.3s ease;
  margin-bottom: 16px;
}

.kpi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.kpi-card-0 {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.kpi-card-1 {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.kpi-card-2 {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.kpi-card-3 {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.kpi-content {
  padding: 16px;
}

.kpi-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.kpi-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.kpi-icon {
  color: white;
}

.kpi-label {
  font-size: 14px;
  font-weight: 600;
  opacity: 0.95;
  flex: 1;
}

.kpi-main-value {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 8px;
  line-height: 1.2;
}

.kpi-unit {
  font-size: 20px;
  font-weight: 500;
  opacity: 0.9;
  margin-left: 4px;
}

.kpi-secondary-value {
  font-size: 13px;
  opacity: 0.85;
  margin-bottom: 16px;
  font-weight: 500;
}

.kpi-stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.kpi-stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.stat-label {
  opacity: 0.8;
  font-weight: 500;
}

.stat-value {
  font-weight: 700;
  font-size: 13px;
}

/* Responsive */
@media (max-width: 768px) {
  .kpi-content {
    padding: 12px;
  }

  .kpi-main-value {
    font-size: 24px;
  }

  .kpi-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .kpi-icon-wrapper {
    width: 36px;
    height: 36px;
    font-size: 18px;
  }
}

@media (max-width: 480px) {
  .kpi-main-value {
    font-size: 20px;
  }

  .kpi-secondary-value {
    font-size: 12px;
  }
}
</style>
